import { Check, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';

interface InvoiceFilterButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  title: string;
  options: Array<{id: string, name: string}>;
  selectedOptions: Array<{id: string, name: string}>;
  onSelectionChange: (selectedOptions: Array<{id: string, name: string}>) => void;
}

const InvoiceFilterButton: React.FC<InvoiceFilterButtonProps> = ({ icon, title, options, selectedOptions, onSelectionChange, ...props }) => {

  //   const [selectedOptions, setSelectedOptions] = useState<{id: string, name: string}[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<{id: string, name: string}[]>(options || []);
  const [filterSelected, setFilterSelected] = useState(false);

  const handleSelectOption = (e: React.MouseEvent<HTMLDivElement>, option: {id: string, name: string}) => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedOptions.some(selectedOption => selectedOption.id === option.id)) {
      onSelectionChange(selectedOptions.filter(selectedOption => selectedOption.id !== option.id));
    } else {
      onSelectionChange([...selectedOptions, option]);
    }
  };

  const handleClearSelected = (e: React.MouseEvent<HTMLButtonElement>) => {
    setSearchValue('');
    onSelectionChange([]);
    setFilterSelected(false);
    setFilteredOptions(options || []);
  };

  const handleFilterSelected = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFilterSelected(!filterSelected);
  };

  useEffect(() => {
    let filteredOptions = options;
    if (filterSelected) {
      filteredOptions = options?.filter((option) => selectedOptions.some(selectedOption => selectedOption.id === option.id)) || [];
    }
    if (searchValue) {
      filteredOptions = filteredOptions?.filter((option) => option.name.toLowerCase().includes(searchValue.toLowerCase())) || [];
    }
    setFilteredOptions(filteredOptions || []);
  }, [filterSelected, searchValue, options]);

  useEffect(() => {
    onSelectionChange(selectedOptions);
  }, [selectedOptions, onSelectionChange]);

  return (
    <div className='relative'>
      {selectedOptions.length > 0 && (
        <div className='absolute w-4 h-4 bg-red-502 right-0 top-0 rounded-full text-white text-xs font-bold flex items-center justify-center'>
          {selectedOptions.length}
        </div>
      )
      }
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={selectedOptions.length > 0 ? 'primary' : 'outline'}
            size="sm"
            {...props}
          >
            {icon && (
              <span className="mr-2">
                {icon}
              </span>
            )}
            {title}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>

          <div className="flex flex-row gap-2 items-center">
            <input
              type="text"
              placeholder="Search"
              className="w-full h-10 border-b border-gray-200 p-2 focus:outline-none focus:ring-0 focus:border-gray-300 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
          <div className="flex flex-row gap-0 items-center justify-between border-b border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              className="border-none hover:bg-transparent hover:text-gray-400"
              onClick={(e) => handleClearSelected(e)}
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`border-none rounded-none hover:bg-transparent hover:text-gray-400 ${filterSelected ? 'bg-gray-201' : ''}`}
              onClick={(e) => handleFilterSelected(e)}
            >
              <Check className="h-4 w-4" />
              Selected
            </Button>
          </div>
          {filteredOptions?.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onClick={(e) => handleSelectOption(e, option)}
            >
              <span className="mr-2 flex items-center gap-2">
                {selectedOptions.some(selectedOption => selectedOption.id === option.id) ? <Check className="h-4 w-4" /> : <span className="h-4 w-4" />}
                {option.name}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default InvoiceFilterButton;
